import { useParams } from 'react-router'
import { SUPPORTED_LANGS, type SupportedLang } from './use-localized-navigate'

/**
 * Return type for useLangParam hook
 */
export interface UseLangParamReturn {
  /** Current language from URL, validated and fallback to 'en' if invalid */
  lang: SupportedLang
  /** Whether the URL lang param is a valid supported language */
  isValidLang: boolean
  /** List of supported languages */
  supportedLangs: typeof SUPPORTED_LANGS
}

/**
 * Hook to extract and validate the language from URL params.
 *
 * Centralizes language detection from URL for use in components
 * that need to know the current language context.
 *
 * Usage:
 * ```typescript
 * const { lang, isValidLang, supportedLangs } = useLangParam()
 *
 * // URL: /fr/dashboard → lang = 'fr', isValidLang = true
 * // URL: /de/dashboard → lang = 'en', isValidLang = false (fallback)
 * // URL: /dashboard    → lang = 'en', isValidLang = false (no prefix)
 * ```
 */
export function useLangParam(): UseLangParamReturn {
  const { lang } = useParams<{ lang: string }>()

  // Check if the URL param is a valid supported language
  const isValidLang =
    lang !== undefined && SUPPORTED_LANGS.includes(lang as SupportedLang)

  // Use validated lang or fallback to 'en'
  const validLang: SupportedLang = isValidLang ? (lang as SupportedLang) : 'en'

  return {
    lang: validLang,
    isValidLang,
    supportedLangs: SUPPORTED_LANGS,
  }
}
